import { useEffect, useRef, useState } from 'react';
import {
  deleteProceduraFile,
  getMediaUrl,
  listProceduraFiles,
  uploadProceduraFile,
} from '../../services/proceduraFiles';
import { FilterPill } from './FilterPill';

export default function ProceduraFileSection({
  procId, showToast, pendingFiles, onPendingFilesChange,
  tipOptions = null,   // if set: string[] → shows a dropdown instead of free text
  keyPrefix  = 'staged', // prefix for deferred file keys — use different values per section
  label      = null,   // optional section label shown above the button row
  filterFn   = null,   // optional filter fn for saved files: (file) => bool
}) {
  const deferred = !procId;
  const [files, setFiles] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const rowCounter = useRef(0);

  useEffect(() => {
    if (!procId) return;
    listProceduraFiles(procId)
      .then(d => setFiles(d.files || []))
      .catch(() => showToast && showToast('Eroare la incarcarea fisierelor'));
  }, [procId, showToast]);

  const addRow = () => {
    rowCounter.current += 1;
    setPendingRows(r => [...r, { rowId: rowCounter.current, selectVal: '', customNume: '', uploading: false }]);
  };

  const updateRow = (rowId, patch) =>
    setPendingRows(r => r.map(row => row.rowId === rowId ? { ...row, ...patch } : row));

  const removeRow = (rowId) =>
    setPendingRows(r => r.filter(row => row.rowId !== rowId));

  // Resolve the actual file name to save: custom text when "Altele" selected, else the dropdown value
  const rowDenumire = (row) =>
    row.selectVal === 'Altele' ? (row.customNume || '').trim() : row.selectVal;

  const handleFileSelect = async (rowId, file) => {
    if (!file) return;
    const row = pendingRows.find(r => r.rowId === rowId);
    if (!row) return;
    const denumire = rowDenumire(row);

    if (deferred) {
      removeRow(rowId);
      onPendingFilesChange([...(pendingFiles || []), { key: `${keyPrefix}-${rowId}`, denumire, file }]);
      return;
    }

    updateRow(rowId, { uploading: true });
    try {
      const data = await uploadProceduraFile(procId, denumire, file);
      setFiles(prev => [data.file, ...prev]);
      removeRow(rowId);
      showToast && showToast('✓ Fisier incarcat');
    } catch {
      showToast && showToast('Eroare la incarcarea fisierului');
      updateRow(rowId, { uploading: false });
    }
  };

  // Always include "Altele" at the end of tipOptions
  const allTipOptions = tipOptions
    ? (tipOptions.includes('Altele') ? tipOptions : [...tipOptions, 'Altele'])
    : null;

  const handleDelete = async (fileId) => {
    try {
      await deleteProceduraFile(fileId);
      setFiles(prev => prev.filter(f => f.id !== fileId));
      setConfirmDel(null);
      showToast && showToast('Fisier sters');
    } catch {
      showToast && showToast('Eroare la stergere');
    }
  };

  const removeStagedFile = (key) => {
    onPendingFilesChange((pendingFiles || []).filter(f => f.key !== key));
  };

  const isImage = name => /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(name || '');

  if (deferred && !onPendingFilesChange) {
    return (
      <p className="f-hint" style={{ marginTop: 6 }}>
        Fisierele de reprezentare pot fi adaugate dupa salvarea procedurii.
      </p>
    );
  }

  const myPending = (pendingFiles || []).filter(f => f.key.startsWith(`${keyPrefix}-`));
  const savedItems = filterFn ? files.filter(filterFn) : files;
  const displayItems = deferred
    ? myPending.map(f => ({ key: f.key, denumire: f.denumire, name: f.file.name, saved: false }))
    : savedItems.map(f => ({ key: f.id, denumire: f.denumire, name: f.filename, url: f.url, saved: true }));

  return (
    <div className="proc-file-section">
      {label && <div className="tr-divider" style={{ marginBottom: 6 }}>{label}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="btn btn-secondary btn-sm" type="button" onClick={addRow}>
          + Adauga fisier
        </button>
      </div>

      {pendingRows.map(row => (
        <div key={row.rowId} className="doc-pending-row" style={{ flexWrap: 'wrap' }}>
          {allTipOptions ? (
            <>
              <FilterPill
                value={row.selectVal}
                onChange={e => updateRow(row.rowId, { selectVal: e.target.value, customNume: '' })}
              >
                <option value="">-- Tip document --</option>
                {allTipOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </FilterPill>
              {row.selectVal === 'Altele' && (
                <input
                  className="doc-custom-input"
                  type="text"
                  placeholder="Denumire fisier..."
                  value={row.customNume}
                  onChange={e => updateRow(row.rowId, { customNume: e.target.value })}
                  style={{ marginTop: 4 }}
                />
              )}
            </>
          ) : (
            <input
              className="doc-custom-input"
              type="text"
              placeholder="Denumire / descriere fisier..."
              value={row.selectVal}
              onChange={e => updateRow(row.rowId, { selectVal: e.target.value })}
            />
          )}
          <label className={`btn btn-secondary btn-xs doc-file-btn${row.uploading ? ' disabled' : ''}`}>
            {row.uploading ? 'Se incarca...' : 'Alege fisier'}
            <input
              type="file"
              style={{ display: 'none' }}
              disabled={row.uploading}
              onChange={e => handleFileSelect(row.rowId, e.target.files[0])}
            />
          </label>
          <button className="btn btn-ghost btn-xs" type="button" onClick={() => removeRow(row.rowId)}>✕</button>
        </div>
      ))}

      {displayItems.length === 0 && pendingRows.length === 0 && (
        <p className="f-hint" style={{ margin: '4px 0' }}>Niciun fisier adaugat inca.</p>
      )}

      {displayItems.map(item => (
        <div key={item.key} className="doc-saved-row">
          <div className="doc-saved-info">
            {item.saved && isImage(item.name) && item.url && (
              <img
                src={getMediaUrl(item.url)}
                alt=""
                style={{ width: 60, height: 20, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }}
              />
            )}
            {item.denumire && <span className="doc-saved-type">{item.denumire}</span>}
            <span className="doc-saved-name">{item.name}</span>
          </div>
          <div className="doc-saved-actions">
            {item.saved ? (
              <>
                <a
                  className="btn btn-ghost btn-xs"
                  href={getMediaUrl(item.url)}
                  download={item.name}
                  target="_blank"
                  rel="noreferrer"
                >
                  ↓ Descarca
                </a>
                {confirmDel === item.key ? (
                  <>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>Stergi?</span>
                    <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={() => handleDelete(item.key)}>Da</button>
                    <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(null)}>Nu</button>
                  </>
                ) : (
                  <button className="btn btn-ghost btn-xs" onClick={() => setConfirmDel(item.key)}>✕</button>
                )}
              </>
            ) : (
              <>
                <span className="f-hint" style={{ whiteSpace: 'nowrap' }}>se incarca la salvare</span>
                <button className="btn btn-ghost btn-xs" onClick={() => removeStagedFile(item.key)}>✕</button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
